import { ChangeDetectorRef, Component, Inject, OnInit } from '@angular/core';
import { MsalBroadcastService, MsalGuardConfiguration, MsalService, MSAL_GUARD_CONFIG } from '@azure/msal-angular';
import { AccountInfo, AuthenticationResult, EventMessage, EventType, InteractionStatus, InteractionType, RedirectRequest, SsoSilentRequest } from '@azure/msal-browser';
import { filter, Subject, takeUntil } from 'rxjs';
import { b2cPolicies, protectedResources } from '../../../auth-config';
import { IdTokenClaims, PromptValue } from '@azure/msal-common';
import { TranslateService } from '@ngx-translate/core';
import { Location } from '@angular/common';
import { HttpStatusCode } from '@angular/common/http';
import { MenuService } from '../services/menu.service';
import { DataService } from '../../../shared-services/cache-data.service';

type IdTokenClaimsWithPolicyId = IdTokenClaims & {
  acr?: string,
  tfp?: string,
};
@Component({
  selector: 'app-master-layout',
  templateUrl: './master-layout.component.html',
  styleUrls: ['./master-layout.component.scss']
})
export class MasterLayoutComponent implements OnInit {

  isIframe = false;
  loginDisplay = false;
  private readonly _destroying$ = new Subject<void>();
  isTerminated: boolean = false;
  isNotAllowed: boolean = false;
  constructor(
    @Inject(MSAL_GUARD_CONFIG) private msalGuardConfig: MsalGuardConfiguration,
    private authService: MsalService,
    private translate: TranslateService,
    private _dataService: DataService,
    public location: Location,
    public service: MenuService,
    private cdr: ChangeDetectorRef,
    private msalBroadcastService: MsalBroadcastService
  ) {

    const cssLang = document.getElementsByTagName('html')[0];
    let currentLanguage = (localStorage.getItem("lang") ?? 'en');
    let htmlCSS = currentLanguage === 'en' ? 'ltr' : 'rtl';
    translate.setDefaultLang(currentLanguage);
    translate.use(currentLanguage);
    cssLang.setAttribute('lang', currentLanguage);
    cssLang.setAttribute('class', htmlCSS);
    localStorage.setItem("lang", currentLanguage);
  }

  async ngOnInit() {
    if (!this.location.path().includes('CPO/page/Add')) {

      this.isIframe = window !== window.parent && !window.opener;
      this.setLoginDisplay();

      this.authService.instance.enableAccountStorageEvents(); // Optional - This will enable ACCOUNT_ADDED and ACCOUNT_REMOVED events emitted when a user logs in or out of another tab or window

      /**
       * You can subscribe to MSAL events as shown below. For more info,
       * visit: https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-angular/docs/v2-docs/events.md
       */
      this.msalBroadcastService.msalSubject$
        .pipe(
          filter((msg: EventMessage) => msg.eventType === EventType.ACCOUNT_ADDED || msg.eventType === EventType.ACCOUNT_REMOVED),
        )
        .subscribe((result: EventMessage) => {
          if (this.authService.instance.getAllAccounts().length === 0) {
            window.location.pathname = "/";
          } else {
            this.setLoginDisplay();
          }
        });

      this.msalBroadcastService.inProgress$
        .pipe(
          filter((status: InteractionStatus) => status === InteractionStatus.None),
          takeUntil(this._destroying$)
        )
        .subscribe(() => {
          this.setLoginDisplay();
          this.checkAndSetActiveAccount();
        })

      this.msalBroadcastService.msalSubject$
        .pipe(
          filter((msg: EventMessage) => msg.eventType === EventType.LOGIN_SUCCESS
         /*   || msg.eventType === EventType.ACQUIRE_TOKEN_SUCCESS*/
            || msg.eventType === EventType.SSO_SILENT_SUCCESS),
          takeUntil(this._destroying$)
        )
        .subscribe((result: EventMessage) => {

          let payload = result.payload as AuthenticationResult;
          let idtoken = payload.idTokenClaims as IdTokenClaimsWithPolicyId;

          if (idtoken.acr === b2cPolicies.names.signUpSignIn || idtoken.tfp === b2cPolicies.names.signUpSignIn) {
            this.authService.instance.setActiveAccount(payload.account);
          }
          scopes: [protectedResources.apiMenueList.scopes.read]

          /**
           * For the purpose of setting an active account for UI update, we want to consider only the auth response resulting
           * from SUSI flow. "acr" claim in the id token tells us the policy (NOTE: newer policies may use the "tfp" claim instead).
           * To learn more about B2C tokens, visit https://docs.microsoft.com/en-us/azure/active-directory-b2c/tokens-overview
           */
          if (idtoken.acr === b2cPolicies.names.editProfile || idtoken.tfp === b2cPolicies.names.editProfile) {

            // retrieve the account from initial sing-in to the app
            const originalSignInAccount = this.authService.instance.getAllAccounts()
              .find((account: AccountInfo) =>
                account.idTokenClaims?.oid === idtoken.oid
                && account.idTokenClaims?.sub === idtoken.sub
                && ((account.idTokenClaims as IdTokenClaimsWithPolicyId).acr === b2cPolicies.names.signUpSignIn
                  || (account.idTokenClaims as IdTokenClaimsWithPolicyId).tfp === b2cPolicies.names.signUpSignIn)
              );

            let signUpSignInFlowRequest: SsoSilentRequest = {
              authority: b2cPolicies.authorities.signUpSignIn.authority,
              account: originalSignInAccount
            };

            // silently login again with the signUpSignIn policy
            this.authService.ssoSilent(signUpSignInFlowRequest);
          }


          return result;
        });

      this.isNotAllowed = false;
      this.isTerminated = false;
      this._dataService.menu.subscribe(async(x: any) => {
        if (x) {
          await this.getUserInfo();
        }
      });
    }
  }
  async getUserInfo() {
    if (!this.location.path().includes('CPO/page/Add')) {
      this.service.GetUserInfo().then((x: any) => {
        if (x.code == HttpStatusCode.Ok && x.data) {
          this.isNotAllowed = false;
          this.isTerminated = false;
         
          this.updateView();
        }
        else if (x.code == 404) {
          this.service.GetUserInfo().then((x: any) => {
            if (x.code == HttpStatusCode.Ok && x.data) {
              this.isNotAllowed = false;
              this.isTerminated = false;
              this.updateView();
            }
            else {
              this.isNotAllowed = true;
              this.isTerminated = true;
              this.updateView();

            }
          });
        }
        else {
          this.isNotAllowed = true;
          this.isTerminated = true;
          this.updateView();

        }

      });
      this.updateView();

    }
    
  }

  


  setLoginDisplay() {
    this.loginDisplay = this.authService.instance.getAllAccounts().length > 0;
  }

  checkAndSetActiveAccount() {
    /**
     * If no active account set but there are accounts signed in, sets first account to active account
     * To use active account set here, subscribe to inProgress$ first in your component
     * Note: Basic usage demonstrated. Your app may require more complicated account selection logic
     */
    let activeAccount = this.authService.instance.getActiveAccount();

    if (!activeAccount && this.authService.instance.getAllAccounts().length > 0) {
      let accounts = this.authService.instance.getAllAccounts();
      // add your code for handling multiple accounts here
      this.authService.instance.setActiveAccount(accounts[0]);
    }
    if (activeAccount) {
      sessionStorage.setItem('userName', activeAccount.username ?? '');
    }
  }

  login(userFlowRequest?: RedirectRequest) {
    if (this.msalGuardConfig.authRequest) {
      this.authService.loginRedirect({ ...this.msalGuardConfig.authRequest, ...userFlowRequest } as RedirectRequest);
    } else {
      this.authService.loginRedirect(userFlowRequest);
    }

  }

  logout() {
    const activeAccount =
      this.authService.instance.getActiveAccount() ||
      this.authService.instance.getAllAccounts()[0];

    if (this.msalGuardConfig.interactionType === InteractionType.Popup) {
      this.authService.logoutPopup({
        account: activeAccount,
      });
    } else {
      this.authService.logoutRedirect({
        account: activeAccount,
      });
    }
  }

  editProfile() {
    let editProfileFlowRequest: RedirectRequest = {
      authority: b2cPolicies.authorities.editProfile.authority,
      scopes: [],
    };

    this.login(editProfileFlowRequest);
  }

  // unsubscribe to events when component is destroyed
  ngOnDestroy(): void {
    this._destroying$.next(undefined);
    this._destroying$.complete();
  }

  updateView() {
    this.cdr.markForCheck();

  }
}
